import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    getDocs,
    QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Submission } from '../types';

const PAGE_SIZE = 50;

/**
 * useSubmissions — Paginated submission fetching hook.
 *
 * Same pattern as useParticipants. Replaces unbounded onSnapshot for submissions.
 * Supports optional filtering by participantId for participant-scoped views.
 *
 * Usage:
 *   const { submissions, fetchNextPage, hasMore, loading } = useSubmissions(projectId);
 *   const { submissions } = useSubmissions(projectId, { participantId });
 */

interface UseSubmissionsOptions {
    participantId?: string;
    mediaType?: string;
    taskIds?: string[];
}

export function useSubmissions(projectId: string, organizationId: string | null, claimsRole: string | null, options?: UseSubmissionsOptions) {
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [cursor, setCursor] = useState<QueryDocumentSnapshot | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const taskIdsStr = options?.taskIds ? JSON.stringify(options.taskIds) : undefined;

    const fetchPage = useCallback(async (reset = false) => {
        if (loading) return;

        // Missing scope (no project selected / no orgId yet) — settle instead
        // of spinning forever: these returns used to skip the finally that
        // clears initialLoading, so /data mounted without a project showed
        // an eternal loader. The reset effect refetches when scope arrives.
        if (!projectId || (!organizationId && claimsRole !== 'superadmin')) {
            setInitialLoading(false);
            return;
        }

        setLoading(true);

        try {
            let q;

            // Build the base constraints
            const constraints: any[] = [
                where('projectId', '==', projectId)
            ];

            // Org-scoped filter — superadmins skip this to see all orgs
            if (organizationId) {
                constraints.push(where('organizationId', '==', organizationId));
            }

            if (options?.participantId) {
                constraints.push(where('participantId', '==', options.participantId));
            }

            if (options?.mediaType && options.mediaType !== 'all') {
                constraints.push(where('mediaType', '==', options.mediaType));
            }

            if (options?.taskIds) {
                if (options.taskIds.length === 0) {
                    // Empty array would crash Firestore's 'in' query. Return empty results immediately.
                    setSubmissions([]);
                    setHasMore(false);
                    setCursor(null);
                    setLoading(false);
                    setInitialLoading(false);
                    return;
                }

                let validTaskIds = options.taskIds;
                if (validTaskIds.length > 30) {
                    console.warn(`[useSubmissions] Task group has ${validTaskIds.length} tasks. Firestore 'in' queries are limited to 30. Truncating to first 30.`);
                    validTaskIds = validTaskIds.slice(0, 30);
                }
                constraints.push(where('taskId', 'in', validTaskIds));
            }

            // Always order by timestamp descending
            constraints.push(orderBy('timestamp', 'desc'));

            if (!reset && cursor) {
                constraints.push(startAfter(cursor));
            }

            constraints.push(limit(PAGE_SIZE));

            q = query(collection(db, 'submissions'), ...constraints);

            const snap = await getDocs(q);
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() as Record<string, unknown> } as Submission));

            setSubmissions(prev => reset ? items : [...prev, ...items]);
            setCursor(snap.docs[snap.docs.length - 1] ?? null);
            setHasMore(snap.docs.length === PAGE_SIZE);
            setError(null);
        } catch (err) {
            // Surface the failure — a swallowed FAILED_PRECONDITION (missing
            // composite index) once made /data render a convincing empty page.
            console.error('[useSubmissions] Fetch error:', err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [projectId, organizationId, claimsRole, options?.participantId, options?.mediaType, taskIdsStr, cursor, loading]);

    // Reset when project, participant, mediaType, or taskIds change
    useEffect(() => {
        setSubmissions([]);
        setCursor(null);
        setHasMore(true);
        setInitialLoading(true);
        fetchPage(true);
    }, [projectId, options?.participantId, options?.mediaType, taskIdsStr]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateLocal = useCallback((id: string, updates: Partial<Submission>) => {
        setSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    }, []);

    return {
        submissions,
        fetchNextPage: () => fetchPage(false),
        hasMore,
        loading,
        initialLoading,
        error,
        count: submissions.length,
        updateLocal,
    };
}
